/**
 * Правка config сайта v2 по полям (01.11, ТЗ 1.17 EMB-019 ч. 1, §3.7; закрытая схема — apps/api/src/utils/widgetV2Config.ts).
 * Каждое верхнее поле §3.7 — своя строка редактора: простые — выбором или строками, составные — JSON этого поля. Черновик —
 * строки; разбор назад даёт config или ошибку поля. Схему, хосты ссылок и TXT-002 проверяет сервер при сохранении (400 с
 * полем — подсвечивается строка поля; предупреждения TXT сохранение не останавливают). Ключи config, которых нет в списке
 * (новые поля сервера), сохраняются как были. Чистые функции, тест — widgetCfg.test.ts.
 */
import type { Obj } from "./widgetTypes";

export type FieldKind = "bool" | "enum" | "lines" | "int" | "json";
export interface CfgField { key: string; label: string; kind: FieldKind; required?: boolean; options?: readonly string[]; nullable?: boolean; hint?: string }

/** Поля §3.7 в порядке экрана (обязательные — как в V2_CONFIG_SCHEMA.required). */
export const CFG_FIELDS: readonly CfgField[] = [
  { key: "domains", label: "Домены сайта (Origin)", kind: "lines", hint: "по одному в строке, до 5; пусто — домен сайта и www." },
  { key: "skin", label: "Кожа", kind: "enum", required: true, options: ["default", "horror"] },
  { key: "disabled", label: "Выключатель: все кнопки — на запасной путь", kind: "bool", required: true },
  { key: "contacts", label: "Контакты: телефон, MAX, Telegram", kind: "json", required: true },
  { key: "sales_hours", label: "Часы продаж", kind: "json", required: true, hint: '{"from":"10:00","to":"22:00"}' },
  { key: "quests", label: "Квесты и порядок", kind: "json", required: true, hint: '[{"id":"sudn","order":1}]' },
  { key: "kinds", label: "Виды заявок", kind: "json", required: true, hint: '["slot","open","group","package"]' },
  { key: "popup_map", label: "Карта окон Tilda (#popup:… → квест)", kind: "json", hint: '{"#popup:psihushka":"psih"}' },
  { key: "legal", label: "Юридические ссылки, условия, операторы", kind: "json", required: true },
  { key: "analytics", label: "Аналитика: Метрика, цели, VK, коллтрекинг", kind: "json", required: true },
  { key: "texts", label: "Замены текстов", kind: "json", required: true, hint: "{}" },
  { key: "tokens", label: "Цвета и размеры кожи (--ub-*)", kind: "json" },
  { key: "packages", label: "Праздничные пакеты", kind: "json" },
  { key: "holidays", label: "Праздничные даты", kind: "json", hint: '["2026-12-31"]' },
  { key: "mc_public", label: "Мастер-классы на сайте", kind: "json", hint: '[{"id":"slim","title":"Слаймы"}]' },
  { key: "ad_label", label: "Пометка рекламы", kind: "json" },
  { key: "group_from", label: "Группа — от скольких человек", kind: "int", hint: "пусто — 15" },
  { key: "card_pii", label: "Карточка ДК: телефон", kind: "enum", options: ["masked", "full"], hint: "пусто — masked" },
  { key: "suspect_hold", label: "Заявка «под вопросом» держит слот", kind: "enum", options: ["hold", "no_hold"], hint: "пусто — hold" },
  { key: "slot_hold_per_ip_day", label: "Слотов с одного IP в сутки", kind: "int", nullable: true, hint: "0 — ни одного: заявки на время только в «Лиды»; 1…10; null — без предела" },
  { key: "ny", label: "НГ-сайт: заявки во вкладку «Новый год» ДК", kind: "bool" },
];
const KEYS = new Set(CFG_FIELDS.map((f) => f.key));

export type Draft = Record<string, string>;
/** config → черновик: у отсутствующего ключа — пустая строка. */
export function toDraft(cfg: Obj | null): Draft {
  const out: Draft = {};
  for (const f of CFG_FIELDS) {
    const v = cfg?.[f.key];
    if (v === undefined) out[f.key] = "";
    else if (f.kind === "lines") out[f.key] = Array.isArray(v) ? v.map(String).join("\n") : "";
    else if (f.kind === "bool" || f.kind === "enum" || f.kind === "int") out[f.key] = v === null ? "null" : String(v);
    else out[f.key] = JSON.stringify(v, null, 2);
  }
  return out;
}

export type Parsed = { ok: true; config: Obj } | { ok: false; field: string; why: string };
/** Черновик → config поверх исходного (неизвестные ключи исходного остаются). Поле, которое не трогали (строка та же, что дал
 *  исходный), — значение исходного как есть: domains: [] не превращается в «нет ключа» и «изменено» без правки (ревью b235d4e4).
 *  Пусто — ключа нет (обязательное — ошибка). */
export function fromDraft(d: Draft, original: Obj | null): Parsed {
  const out: Obj = Object.fromEntries(Object.entries(original ?? {}).filter(([k]) => !KEYS.has(k)));
  const before = original ? toDraft(original) : null;
  for (const f of CFG_FIELDS) {
    if (original && before && Object.hasOwn(original, f.key) && (d[f.key] ?? "") === before[f.key]) { out[f.key] = original[f.key]; continue; }
    const raw = (d[f.key] ?? "").trim();
    if (raw === "") {
      if (f.required) return { ok: false, field: f.key, why: "обязательное поле" };
      continue;
    }
    if (f.kind === "bool") {
      if (raw !== "true" && raw !== "false") return { ok: false, field: f.key, why: "да или нет" };
      out[f.key] = raw === "true";
    } else if (f.kind === "enum") {
      if (!f.options?.includes(raw)) return { ok: false, field: f.key, why: `одно из: ${f.options?.join(", ")}` };
      out[f.key] = raw;
    } else if (f.kind === "int") {
      if (raw === "null" && f.nullable) out[f.key] = null;
      else if (/^-?\d{1,6}$/.test(raw)) out[f.key] = Number(raw);
      else return { ok: false, field: f.key, why: f.nullable ? "целое число или null" : "целое число" };
    } else if (f.kind === "lines") {
      const items = raw.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
      if (items.length) out[f.key] = items;
    } else {
      try { out[f.key] = JSON.parse(raw) as unknown; } catch { return { ok: false, field: f.key, why: "не JSON — проверьте скобки и кавычки" }; }
    }
  }
  return { ok: true, config: out };
}

const canon = (v: unknown): string => JSON.stringify(v, (_k, x: unknown) => (x && typeof x === "object" && !Array.isArray(x)
  ? Object.fromEntries(Object.entries(x as Obj).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) : x)) ?? "undefined";
/** Верхние ключи, которые меняются при сохранении (для подписи «изменено»). */
export function changedKeys(before: Obj | null, after: Obj): string[] {
  const keys = new Set([...Object.keys(before ?? {}), ...Object.keys(after)]);
  return [...keys].filter((k) => canon(before?.[k]) !== canon(after[k])).sort();
}

export interface SelectOption { value: string; label: string; disabled?: boolean }
/** Выбор поля bool/enum: у необязательного — «не задано»; у обязательного без значения — «выберите» (недоступный пункт), чтобы
 *  экран не показывал первый пункт при пустом черновике (ревью b235d4e4: «default» и «да» на экране, а сохранение — «обязательное поле»). */
export function selectOptions(f: CfgField, value: string): SelectOption[] {
  const opts = f.kind === "bool" ? ["true", "false"] : [...(f.options ?? [])];
  const head: SelectOption[] = !f.required ? [{ value: "", label: "— не задано —" }] : value === "" || !opts.includes(value) ? [{ value: "", label: "— выберите —", disabled: true }] : [];
  return [...head, ...opts.map((o) => ({ value: o, label: o === "true" ? "да" : o === "false" ? "нет" : o }))];
}

/** Черновики равны по всем полям редактора. */
export const sameDraft = (a: Draft, b: Draft): boolean => CFG_FIELDS.every((f) => (a[f.key] ?? "") === (b[f.key] ?? ""));
/** Черновик после перечитывания config (любое действие карточки перечитывает сайт): только что сохранили или правки нет (черновик
 *  равен прежнему исходному) — новый исходный; есть несохранённая правка — она остаётся (ревью b235d4e4). */
export const nextDraft = (draft: Draft, before: Draft, after: Draft, justSaved: boolean): Draft =>
  (justSaved || sameDraft(draft, before) ? after : draft);

/** Поле ответа сервера (400 field: «legal.policy_url», «domain») → строка редактора. */
export function fieldOwner(field: string): string {
  const top = field.split(".")[0] ?? field;
  return top === "domain" ? "domains" : top;
}
